// Typed client for the sandbox-demo admin access endpoints (users / groups /
// shares). Every call throws an Error carrying the backend's `error` message so
// the page can surface it verbatim.
import {
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useMemo } from 'react';
import type { SandboxStatus } from '../sandbox/model';

export type AccessLevel = 'read' | 'write';
export type PrincipalType = 'user' | 'group';

export type AdminRequest = {
  id: string;
  sandboxName: string;
  status: SandboxStatus;
  ownerEntityRef: string;
  ownerDisplayName: string;
  projectType: 'sandbox' | 'low-code';
  accountName?: string;
  accountId?: string;
  createdAt: string;
  updatedAt: string;
  deletable: boolean;
};

export type DirectoryUser = {
  entityRef: string;
  displayName?: string;
  email?: string;
  firstSeenAt?: string;
  lastSeenAt?: string;
  isAdmin: boolean;
  builtInAdmin: boolean;
  groupIds: string[];
};

export type AccessGroup = {
  id: string;
  name: string;
  description: string;
  members: string[];
  createdAt: string;
  updatedAt: string;
};

export type SandboxShare = {
  id: string;
  requestId: string;
  principalType: PrincipalType;
  principalRef: string;
  access: AccessLevel;
  createdBy: string;
  createdAt: string;
};

export type SecretStatus = {
  id: string;
  label: string;
  description: string;
  parameter: string;
  isSet: boolean;
  version?: number;
  lastModified?: string;
  hint?: string;
  error?: string;
};

export type TemplateVersion = {
  sha: string;
  ref?: string;
  message?: string;
  committedAt?: string;
  pulledAt?: string;
  pulledBy?: string;
  source: 'built-in' | 'pulled';
};

export type TemplateState = {
  repo: string;
  active: TemplateVersion;
  builtIn: TemplateVersion;
  activeRuns: number;
};

export type UpstreamCommit = { sha: string; message: string; author?: string; date?: string };

export type UpstreamStatus = {
  repo: string;
  branch: string;
  head: UpstreamCommit;
  aheadBy: number;
  behindBy: number;
  commits: UpstreamCommit[];
};

export const useAccessApi = () => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const base = `${configApi.getString('backend.baseUrl')}/api/sandbox-demo`;

  return useMemo(() => {
    const call = async <T>(
      method: string,
      path: string,
      body?: unknown,
    ): Promise<T> => {
      const response = await fetchApi.fetch(`${base}${path}`, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => undefined)) as
          | { error?: string }
          | undefined;
        throw new Error(payload?.error ?? `Request failed with ${response.status}`);
      }
      return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
    };
    const q = encodeURIComponent;

    return {
      me: () => call<{ entityRef: string; isAdmin: boolean }>('GET', '/access/me'),
      recordProfile: (profile: { displayName?: string; email?: string }) =>
        call('POST', '/me/profile', profile),

      listRequests: () => call<AdminRequest[]>('GET', '/admin/requests'),
      deleteRequest: (id: string) => call<void>('DELETE', `/admin/requests/${q(id)}`),

      listUsers: () => call<DirectoryUser[]>('GET', '/admin/users'),
      setAdmin: (entityRef: string, isAdmin: boolean) =>
        call<void>('POST', '/admin/users/admin', { entityRef, isAdmin }),
      addUser: (identifier: string, displayName?: string) =>
        call('POST', '/admin/users', { identifier, displayName }),
      removeUser: (entityRef: string) =>
        call<void>('DELETE', `/admin/users?entityRef=${q(entityRef)}`),

      listGroups: () => call<AccessGroup[]>('GET', '/admin/groups'),
      createGroup: (name: string, description: string) =>
        call<AccessGroup>('POST', '/admin/groups', { name, description }),
      updateGroup: (id: string, patch: { name?: string; description?: string }) =>
        call<AccessGroup>('PATCH', `/admin/groups/${q(id)}`, patch),
      deleteGroup: (id: string) => call<void>('DELETE', `/admin/groups/${q(id)}`),
      addMember: (groupId: string, entityRef: string) =>
        call<AccessGroup>('POST', `/admin/groups/${q(groupId)}/members`, { entityRef }),
      removeMember: (groupId: string, entityRef: string) =>
        call<AccessGroup>(
          'DELETE',
          `/admin/groups/${q(groupId)}/members?entityRef=${q(entityRef)}`,
        ),

      listShares: () => call<SandboxShare[]>('GET', '/admin/shares'),
      share: (
        requestId: string,
        principalType: PrincipalType,
        principalRef: string,
        access: AccessLevel,
      ) =>
        call<SandboxShare>('POST', '/admin/shares', {
          requestId,
          principalType,
          principalRef,
          access,
        }),
      unshare: (id: string) => call<void>('DELETE', `/admin/shares/${q(id)}`),

      listSecrets: () => call<SecretStatus[]>('GET', '/admin/platform/secrets'),
      setSecret: (id: string, value: string, skipVerify = false) =>
        call<SecretStatus[]>('PUT', `/admin/platform/secrets/${q(id)}`, { value, skipVerify }),
      templateState: () => call<TemplateState>('GET', '/admin/platform/template'),
      templateUpstream: (branch?: string) =>
        call<UpstreamStatus>(
          'GET',
          `/admin/platform/template/upstream${branch ? `?branch=${q(branch)}` : ''}`,
        ),
      pullTemplate: (ref: string, force = false) =>
        call<TemplateState>('POST', '/admin/platform/template/pull', { ref, force }),
      revertTemplate: (force = false) =>
        call<TemplateState>('POST', '/admin/platform/template/revert', { force }),
    };
  }, [base, fetchApi]);
};

export type AccessApi = ReturnType<typeof useAccessApi>;
